import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, Eye, ArrowRight } from 'lucide-react'
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { generateUniqueSlug } from '../utils/slugify'
import Loading from '../components/Loading'
import HeroSection from '../components/HeroSection'
import FeaturedTours from '../components/FeaturedTours'
import PopularDestinations from '../components/PopularDestinations'
import WhyChooseUs from '../components/WhyChooseUs'
import Testimonials from '../components/Testimonials'
import Newsletter from '../components/Newsletter'

const Home = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [blogPosts, setBlogPosts] = useState([])
  const [loadingBlog, setLoadingBlog] = useState(true)

  useEffect(() => {
    loadBlogPosts()
  }, [])

  const loadBlogPosts = async () => {
    try {
      setLoadingBlog(true)
      const postsQuery = query(
        collection(db, 'POSTS'),
        orderBy('publishedAt', 'desc')
      )
      const snapshot = await getDocs(postsQuery)
      const postsData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(post => post.disabled !== true) // Filter in code
        .slice(0, 4) // Limit to 4
      setBlogPosts(postsData)
    } catch (error) {
      console.error('Error loading blog posts:', error)
    } finally {
      setLoadingBlog(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return ''
    try {
      const d = date.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (e) {
      return ''
    }
  }

  return (
    <div>
      <HeroSection />
      <FeaturedTours />
      <PopularDestinations />

      {/* Blog Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              📰 {t('home.blog.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.blog.subtitle')}
            </p>
          </div>

          {loadingBlog ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">{t('home.blog.loading')}</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('home.blog.noPosts')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {blogPosts.map((post) => {
                const postSlug = generateUniqueSlug(post.title, post.id)
                return (
                  <div
                    key={post.id}
                    onClick={() => navigate(`/blog/${postSlug}`)}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
                  >
                    {post.imageUrl && (
                      <div className="relative overflow-hidden h-48">
                        <img
                          src={post.imageUrl}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {post.category && (
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                              {post.category}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(post.publishedAt)}</span>
                        </div>
                        {post.readTime && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{post.readTime}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span>{t('home.blog.viewAll')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <WhyChooseUs />
      <Testimonials />
      <Newsletter />
    </div>
  )
}

export default Home
